import React, { useState } from 'react';
import {
  MdArrowBack, MdPictureAsPdf, MdReceiptLong, MdDownload, MdAccountBalance,
  MdDescription, MdInsertDriveFile,
} from 'react-icons/md';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const buildStatements = () => {
  // Rolling 12 months of statements + current year tax slips, with the
  // newest entries first.
  const today = new Date();
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i - 1, 1);
    out.push({
      id: `stmt-${d.getFullYear()}-${d.getMonth()}`,
      kind: 'statement',
      title: `${MONTHS[d.getMonth()]} ${d.getFullYear()} Statement`,
      sub: 'TD All-Inclusive Banking',
      size: '184 KB',
      date: d.toISOString().slice(0, 10),
    });
  }
  return out;
};

const TAX_SLIPS = [
  { id: 't5-2025', kind: 'tax', title: 'T5 Statement of Investment Income', sub: '2025 tax year', size: '92 KB',  date: '2026-02-22' },
  { id: 't4a-2025', kind: 'tax', title: 'T4A — RESP Withdrawal',            sub: '2025 tax year', size: '76 KB',  date: '2026-02-22' },
  { id: 'rrsp-2025', kind: 'tax', title: 'RRSP Contribution Receipt',        sub: 'Mar–Dec 2025',  size: '54 KB',  date: '2026-01-15' },
  { id: 'rrsp-firstsixty', kind: 'tax', title: 'RRSP Contribution Receipt',  sub: 'First 60 days 2026', size: '52 KB', date: '2026-03-08' },
];

const TABS = [
  { id: 'statements', label: 'Statements' },
  { id: 'tax',        label: 'Tax slips'  },
];

const StatementsScreen = ({ onNavigate, onBack }) => {
  const [tab, setTab] = useState('statements');
  const statements = buildStatements();
  const items = tab === 'statements' ? statements : TAX_SLIPS;

  const groupedByYear = items.reduce((acc, x) => {
    const y = x.date.slice(0, 4);
    (acc[y] = acc[y] || []).push(x);
    return acc;
  }, {});
  const years = Object.keys(groupedByYear).sort().reverse();

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Statements & Documents</h1>
        </div>
        <MdAccountBalance size={22} style={{ color: 'var(--bh-black)' }} />
      </header>

      <div className="bh-flow-body">
        <div className="bh-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              className={`bh-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {years.map(year => (
          <section key={year}>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>{year}</h2>
            <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
              {groupedByYear[year].map((x, i, arr) => {
                const Icon = x.kind === 'tax' ? MdReceiptLong : MdPictureAsPdf;
                const tint = x.kind === 'tax' ? 'var(--td-amber)' : 'var(--td-red)';
                return (
                  <div
                    key={x.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                      padding: 'var(--space-sm)',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--bh-light-gray)',
                    }}
                  >
                    <span style={{
                      width: 36, height: 36, background: tint, color: 'var(--bh-white)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, borderRadius: 'var(--radius-md)',
                    }}>
                      <Icon size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: 'var(--bh-body)', fontWeight: 600, color: 'var(--bh-black)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {x.title}
                      </div>
                      <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                        {x.sub} · PDF · {x.size}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`Download ${x.title}`}
                      onClick={() => { /* future: trigger /api/documents/:id download */ }}
                      style={{
                        width: 36, height: 36,
                        background: 'transparent',
                        border: '1px solid var(--bh-border)',
                        color: 'var(--bh-black)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', flexShrink: 0,
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <MdDownload size={18} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Other documents</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <button type="button" className="bh-tx-row" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none', borderBottom: '1px solid var(--bh-light-gray)' }}>
              <span style={{ width: 36, height: 36, background: 'var(--td-blue)', color: 'var(--bh-white)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 'var(--radius-md)' }}>
                <MdDescription size={18} />
              </span>
              <div className="bh-tx-row-info" style={{ marginLeft: 'var(--space-sm)' }}>
                <span className="bh-tx-merchant">Mortgage statement</span>
                <span className="bh-tx-date">Annual · Updated quarterly</span>
              </div>
            </button>
            <button type="button" className="bh-tx-row" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none' }}>
              <span style={{ width: 36, height: 36, background: 'var(--td-purple)', color: 'var(--bh-white)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 'var(--radius-md)' }}>
                <MdInsertDriveFile size={18} />
              </span>
              <div className="bh-tx-row-info" style={{ marginLeft: 'var(--space-sm)' }}>
                <span className="bh-tx-merchant">Account agreement</span>
                <span className="bh-tx-date">Latest version</span>
              </div>
            </button>
          </div>
        </section>

        <div className="bh-info-box">
          Statements are kept for 7 years. Tax slips for 2025 will be available by late February 2026.
        </div>
      </div>
    </div>
  );
};

export default StatementsScreen;
